// App.js
import React, { Component, Suspense, lazy } from 'react';
import { HashRouter as Router, Switch, Route, Redirect, withRouter } from 'react-router-dom';

import './App.css';

import Main from './containers/Main/Main';
import PrivateRoute from './components/ProtectedRoute/PrivateRoute';
import RouteErrorBoundary from './components/RouteErrorBoundary';

import 'bootstrap/dist/css/bootstrap.min.css';
// react-super-responsive-table's stylesheet used to be imported here. Its only
// consumer (components/ReactiveTable) was dead code and is deleted, so the CSS
// and the dependency went with it.
import 'react-toastify/dist/ReactToastify.css';

import { persistor, Store } from './redux/Store';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { Provider } from 'react-redux';

import jwt_decode from 'jwt-decode';
import jwtAuthService from './services/jwtAuthService';

import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';

import { OnboardingProvider } from './components/Onboarding/OnboardingProvider';

import { GoogleOAuthProvider } from '@react-oauth/google';

// ─── Route-level code splitting ────────────────────────────────────────────
// Every top-level screen is loaded with React.lazy so the first paint (the
// landing page / sign-in) no longer ships Trader_Form, the scanner, the
// settings hub and the Stripe pricing table with it. The route LIST and its
// ORDER in <Switch> below are unchanged — only the import style is.
//
// Rules if you add a route:
//   * lazy() the screen here, next to its siblings;
//   * the whole <Switch> is already inside ONE <Suspense>, so nothing else
//     needs a fallback;
//   * keep the guards (PrivateRoute / AuthRoute), <Main>, RouteErrorBoundary
//     and the providers EAGER — they render on every route, so splitting them
//     buys nothing and costs a spinner.
const Login = lazy(() => import('./components/Login/Login'));
const VerifyForm = lazy(() => import('./containers/Login/VerifyForm'));
const TempPassword = lazy(() => import('./containers/Login/tempPassword'));
const ResetPasswordForm = lazy(() => import('./containers/Login/ResetPasswordForm'));
// Sign-up renders the same component as sign-in (it switches on the route), so
// it deliberately reuses the same lazy handle rather than making a 2nd chunk.
const Sign_up = Login;

const Prices = lazy(() => import('./containers/Payment/Prices'));
const SettingsLayout = lazy(() => import('./containers/Settings/SettingsLayout'));
const SharedView = lazy(() => import('./containers/Share/SharedView'));

const Portfolio = lazy(() => import('./containers/Portfolio/PortfolioOverview'));
const Trader_form = lazy(() => import('./containers/Trader_Form/Trader_Form'));
const Scanner_form = lazy(() => import('./containers/Scanners/scanner_form'));
// Watchlist + scanner hit ledger (backend wave 3-C feature 13). Note the
// folder is `Scanner/` (singular) — `Scanners/` holds the scanner wizard.
const Watchlist = lazy(() => import('./containers/Scanner/Watchlist'));
// Market-data lake coverage + ticker lookup (edge platform phase 1).
const DataCoverage = lazy(() => import('./containers/Data/DataCoverage'));
const ResearchLayout = lazy(() => import('./containers/Research/ResearchLayout'));
const About = lazy(() => import('./containers/About/About'));

const Landing = lazy(() => import('./containers/Landing_Page/Landing'));
const Terms = lazy(() => import('./components/Terms/Terms'));
const Privacy = lazy(() => import('./components/Privacy/Privacy'));

// Shown while a route chunk is in flight. Deliberately minimal and on the app
// background colour so it reads as "still loading" rather than a broken paint.
const RouteFallback = () => (
  <Box
    role="status"
    aria-live="polite"
    aria-label="Loading"
    sx={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
    }}
  >
    <CircularProgress size={28} />
  </Box>
);

// Matte/steel-blue design system — identical to the Portfolio page tokens.
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#4d8fc7' },
    secondary: { main: '#3f6fb0' },
    success: { main: '#46b88a' },
    error: { main: '#d97b72' },
    warning: { main: '#d9a441' },
    info: { main: '#4d8fc7' },
    background: { default: '#0a0c11', paper: '#0f1218' },
    divider: 'rgba(255,255,255,.06)',
    text: { primary: '#e8edf7', secondary: '#9aa6bd' },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
    subtitle1: { fontSize: 15, fontWeight: 600, color: '#e8edf7', lineHeight: 1.3 },
    subtitle2: { fontSize: 13, fontWeight: 600, color: '#e8edf7', lineHeight: 1.3 },
    body2: { fontSize: 13.5, lineHeight: 1.45 },
    caption: { fontSize: 12, color: '#6f7c96', lineHeight: 1.45 },
    h5: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em', color: '#f4f7fc' },
    h6: { fontSize: 17, fontWeight: 600, color: '#e8edf7' },
  },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { backgroundColor: '#0a0c11', color: '#e8edf7' } } },
    // The handoff card: matte, hairline border, no glow.
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { backgroundColor: '#0f1218', borderColor: 'rgba(255,255,255,.06)', borderRadius: 12, boxShadow: '0 1px 2px rgba(0,0,0,.22)' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 9, lineHeight: 1.3, boxShadow: 'none' },
        sizeSmall: { fontSize: 13, padding: '6px 12px' },
        containedPrimary: {
          color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)',
          '&:hover': { background: 'linear-gradient(180deg,#3d5c7b,#33506b)', transform: 'translateY(-1px)' },
          '&.Mui-disabled': { background: '#13171f', color: '#5e6a82' },
        },
        outlined: { borderColor: 'rgba(255,255,255,.1)', backgroundColor: '#13171f', color: '#cdd6e8', '&:hover': { borderColor: 'rgba(255,255,255,.18)', backgroundColor: '#181d27' } },
        outlinedPrimary: { color: '#9cc3e4', borderColor: 'rgba(77,143,199,.38)', backgroundColor: 'rgba(77,143,199,.08)' },
        outlinedError: { color: '#e0a59d', borderColor: 'rgba(217,123,114,.4)', backgroundColor: 'transparent', '&:hover': { backgroundColor: 'rgba(217,123,114,.1)' } },
        outlinedWarning: { color: '#d9a441', borderColor: 'rgba(217,164,65,.35)', backgroundColor: 'transparent', '&:hover': { backgroundColor: 'rgba(217,164,65,.08)' } },
        outlinedSuccess: { color: '#7fd4ae', borderColor: 'rgba(70,184,138,.4)', backgroundColor: 'rgba(70,184,138,.12)' },
        text: { color: '#9cc3e4' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontFamily: '"IBM Plex Mono", ui-monospace, Menlo, monospace', fontSize: 10.5, letterSpacing: '.04em', textTransform: 'uppercase', borderRadius: 999, height: 22, backgroundColor: '#13171f', border: '1px solid rgba(255,255,255,.08)', color: '#9aa6bd' },
        sizeSmall: { height: 22 },
        label: { paddingLeft: 9, paddingRight: 9 },
        outlined: { backgroundColor: '#13171f', borderColor: 'rgba(255,255,255,.08)', color: '#9aa6bd' },
        colorPrimary: { color: '#9cc3e4', backgroundColor: 'rgba(77,143,199,.12)', borderColor: 'rgba(77,143,199,.38)' },
        colorSuccess: { color: '#46b88a', backgroundColor: 'rgba(70,184,138,.08)', borderColor: 'rgba(70,184,138,.3)' },
        colorError: { color: '#d97b72', backgroundColor: 'rgba(217,123,114,.08)', borderColor: 'rgba(217,123,114,.35)' },
        colorWarning: { color: '#d9a441', backgroundColor: 'rgba(217,164,65,.08)', borderColor: 'rgba(217,164,65,.35)' },
        colorInfo: { color: '#9cc3e4', backgroundColor: 'rgba(77,143,199,.12)', borderColor: 'rgba(77,143,199,.38)' },
        colorSecondary: { color: '#9cc3e4', backgroundColor: 'rgba(63,111,176,.14)', borderColor: 'rgba(63,111,176,.4)' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: 12.5, lineHeight: 1.45, color: '#9aa6bd', padding: '4px 14px', alignItems: 'center' },
        standardInfo: { backgroundColor: 'rgba(77,143,199,.06)', border: '1px solid rgba(77,143,199,.25)', boxShadow: 'inset 2px 0 0 rgba(77,143,199,.5)', '& .MuiAlert-icon': { color: '#9cc3e4' } },
        standardWarning: { backgroundColor: 'rgba(217,164,65,.06)', border: '1px solid rgba(217,164,65,.28)', boxShadow: 'inset 2px 0 0 rgba(217,164,65,.5)', '& .MuiAlert-icon': { color: '#d9a441' } },
        standardError: { backgroundColor: 'rgba(217,123,114,.06)', border: '1px solid rgba(217,123,114,.3)', boxShadow: 'inset 2px 0 0 rgba(217,123,114,.5)', '& .MuiAlert-icon': { color: '#d97b72' } },
        standardSuccess: { backgroundColor: 'rgba(70,184,138,.06)', border: '1px solid rgba(70,184,138,.3)', boxShadow: 'inset 2px 0 0 rgba(70,184,138,.5)', '& .MuiAlert-icon': { color: '#46b88a' } },
      },
    },
    MuiAlertTitle: { styleOverrides: { root: { color: '#e8edf7', fontWeight: 600, fontSize: 13 } } },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: '#9cc3e4', height: 2 } } },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 42, color: '#9aa6bd', '&.Mui-selected': { color: '#bcd6ea' } } } },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: 13, color: '#c9d2e3', borderBottom: '1px solid rgba(255,255,255,.06)', padding: '9px 12px' },
        head: { fontFamily: '"IBM Plex Mono", ui-monospace, Menlo, monospace', fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: '#7b88a2', backgroundColor: 'transparent', fontWeight: 500 },
        stickyHeader: { backgroundColor: '#0f1218' },
      },
    },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: '#181d27', border: '1px solid rgba(255,255,255,.1)', color: '#cdd6e8', fontSize: 12, lineHeight: 1.45 }, arrow: { color: '#181d27' } } },
    MuiDivider: { styleOverrides: { root: { borderColor: 'rgba(255,255,255,.06)' } } },
    MuiLinearProgress: { styleOverrides: { root: { backgroundColor: 'rgba(255,255,255,.06)', borderRadius: 4 }, bar: { borderRadius: 4 } } },
    MuiFormHelperText: { styleOverrides: { root: { fontSize: 11, color: '#6f7c96', marginLeft: 0 } } },
    MuiCheckbox: { styleOverrides: { root: { color: '#5e6a82', '&.Mui-checked': { color: '#9cc3e4' } } } },
    MuiSwitch: { styleOverrides: { root: { '& .Mui-checked': { color: '#9cc3e4' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#4d8fc7' } } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#0b0e15', borderRadius: 9,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4d8fc7', borderWidth: 1 },
        },
        input: { color: '#f2f6ff' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#7b88a2', '&.Mui-focused': { color: '#4d8fc7' } } } },
    MuiSlider: { styleOverrides: { root: { color: '#4d8fc7' } } },
  },
});

const CLOCK_SKEW_MS = 30 * 1000;

// Helper: check if token is expired (with grace period for clock skew)
const isTokenExpired = () => {
  const token = localStorage.getItem('token');
  if (!token) return true;
  try {
    const decoded = jwt_decode(token);
    if (!decoded.exp) return false;
    return decoded.exp * 1000 < Date.now() - CLOCK_SKEW_MS;
  } catch (e) {
    return true;
  }
};

const isAuthenticated = () => {
  return localStorage.getItem('authenticated') === 'true' && !isTokenExpired();
};

// Debounced activity listener — refresh at most once every 30s
let lastRefresh = 0;
const REFRESH_THROTTLE_MS = 30 * 1000;

const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
activityEvents.forEach(event => {
  window.addEventListener(event, () => {
    const now = Date.now();
    if (now - lastRefresh > REFRESH_THROTTLE_MS) {
      lastRefresh = now;
      jwtAuthService.refreshSession();
    }
  }, { passive: true });
});

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// SessionWatcher: forces redirect to /sign-in when session expires
// SessionWatcher: forces redirect to /sign-in when session expires
class SessionWatcherInner extends Component {
  constructor(props) {
    super(props);
    this.state = { expiredAt: 0 };
  }

  componentDidMount() {
    this.interval = setInterval(this.checkSession, 15000);
    // Don't run checkSession immediately on mount — give the app a chance
    // to hydrate redux-persist and finish login flows
    this.mountTime = Date.now();

    window.addEventListener('focus', this.checkSession);
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener('session-expired', this.handleExpiredEvent);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener('focus', this.checkSession);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('session-expired', this.handleExpiredEvent);
  }

  componentDidUpdate(_, prevState) {
    if (this.state.expiredAt && this.state.expiredAt !== prevState.expiredAt) {
      setTimeout(() => this.setState({ expiredAt: 0 }), 0);
    }
  }

  handleVisibility = () => {
    if (document.visibilityState === 'visible') this.checkSession();
  };

  handleStorageChange = (e) => {
    // Only trigger if the token is explicitly removed by another tab
    if (e.key === 'token' && e.oldValue && !e.newValue) {
      this.triggerExpiry();
    }
  };

  handleExpiredEvent = () => {
    this.triggerExpiry();
  };

  checkSession = () => {
    // Grace period: don't fire during the first 5 seconds after mount
    // to let login/hydration finish
    if (Date.now() - this.mountTime < 5000) return;

    const isAuth = localStorage.getItem('authenticated') === 'true';
    if (!isAuth) return;

    // ONLY log out on hard signals:
    // 1. The JWT itself has actually expired (server-issued exp claim)
    // We do NOT check isSessionActive() here because session_start may not
    // be set if the login flow doesn't use jwtAuthService.setSession()
    if (isTokenExpired()) {
      jwtAuthService.logout();
      this.triggerExpiry();
    }
  };

  triggerExpiry = () => {
    this.setState({ expiredAt: Date.now() });
  };

  render() {
    const { location } = this.props;
    const onProtectedRoute = location.pathname.startsWith('/profile');
    if (this.state.expiredAt && onProtectedRoute) {
      return <Redirect to="/sign-in" />;
    }
    return null;
  }
}

const SessionWatcher = withRouter(SessionWatcherInner);

const AuthRoute = ({ component: Component, ...props }) => (
  <Route
    {...props}
    render={(routeProps) => {
      // If they have an expired token sitting around, clean it up
      if (localStorage.getItem('authenticated') === 'true' && isTokenExpired()) {
        jwtAuthService.logout();
      }

      return isAuthenticated() ? (
        <Redirect to="/profile/portfolio" />
      ) : (
        <Component {...routeProps} />
      );
    }}
  />
);

class App extends Component {
  render() {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Provider store={Store}>
          <PersistGate persistor={persistor}>
            <Router basename="/">
              <OnboardingProvider>
              <SessionWatcher />

              {/* ONE Suspense boundary around the whole route table — every
                  screen below is React.lazy, and a per-route boundary would
                  flash a separate spinner for the nested /profile switch. */}
              <Suspense fallback={<RouteFallback />}>
              <Switch>
                <AuthRoute exact path="/" component={Landing} />
                <AuthRoute exact path="/features" component={Landing} />
                <AuthRoute exact path="/about" component={Landing} />
                <AuthRoute exact path="/services" component={Landing} />
                <AuthRoute exact path="/team" component={Landing} />
                <AuthRoute exact path="/contact" component={Landing} />

                <AuthRoute exact path="/sign-in" component={Login} />
                <AuthRoute exact path="/verify" component={VerifyForm} />
                <AuthRoute exact path="/sign-up" component={Sign_up} />
                {/* #/changePassword was an AuthRoute, i.e. reachable only while
                    LOGGED OUT, and it posted {email, oldPassword, password} to
                    the unauthenticated POST /api/changeOfPassword. The
                    logged-in form now lives in Settings → Account (wave 3 D1),
                    which sends {oldPassword, newPassword} and takes the
                    identity from the JWT. The screen is deleted rather than
                    kept in two places; the path redirects so old bookmarks and
                    emails still land somewhere sensible. A logged-OUT visitor
                    falls through PrivateRoute to #/sign-in, which is correct —
                    changing a password you know requires signing in, and the
                    "I don't know it" case is #/tempPassword below. */}
                <Route exact path="/changePassword">
                  <Redirect to="/profile/settings/account" />
                </Route>
                <AuthRoute exact path="/tempPassword" component={TempPassword} />

                {/* Landing page for the emailed password-reset link
                    (crypto-backend-api services/email.js builds
                    `${DOMAIN}/#/resetPassword?token=…`).

                    Deliberately a plain <Route>, not an <AuthRoute>: AuthRoute
                    redirects anyone holding a valid session to
                    /profile/portfolio, which would silently swallow the link
                    for a user who is still signed in on this device (a very
                    common case — "I forgot my password" is usually typed on a
                    phone while the desktop is logged in). It must also render
                    for a signed-OUT user, which it does: nothing here reads the
                    session, and Toolbar/Footer are only mounted inside <Main>
                    under /profile, so neither is on this route. */}
                <Route exact path="/resetPassword" component={ResetPasswordForm} />

                {/* Public read-only share page (crypto-backend-api feature 12).
                    A plain <Route> for the same reason /resetPassword is one,
                    plus a stronger one: the recipient of a share link is
                    usually not a user of this app at all, so PrivateRoute would
                    bounce them to #/sign-in and AuthRoute would bounce a
                    signed-in viewer to /profile/portfolio. Nothing on the page
                    reads the session, and services/http.js strips the
                    Authorization header from GET /api/share/:token so a
                    signed-in viewer never leaks their JWT to it. */}
                <Route path="/share/:token" component={SharedView} />
                {/* A run published to the OOS leaderboard (phase 5 item 5):
                    same public page, loaded from GET /api/leaderboard/:id
                    (also JWT-stripped in services/http.js). */}
                <Route path="/published/:id" component={SharedView} />

                <Route path="/profile">
                  <Main>
                    <RouteErrorBoundary>
                    <Switch>
                      <PrivateRoute path="/profile/portfolio">
                        <Portfolio />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/traderform">
                        <Trader_form />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/scanners">
                        <Scanner_form />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/watchlist">
                        <Watchlist />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/data">
                        <DataCoverage />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/research">
                        <ResearchLayout />
                      </PrivateRoute>
                      {/* /profile/orderhistory removed: the legacy Order_history
                          screen POSTs a raw Binance API key + secret to
                          /api/trade in cleartext form state, and its autotrade
                          endpoints identify the caller by localStorage email
                          rather than the JWT. Superseded by /profile/portfolio
                          and the exchange-key screens. */}
                      <PrivateRoute path="/profile/about">
                        <About />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/terms">
                        <Terms />
                      </PrivateRoute>
                      <PrivateRoute path="/profile/privacy">
                        <Privacy />
                      </PrivateRoute>

                      {/* Settings hub: /profile/settings/{account,tokens,audit,exports}.
                          SettingsLayout owns its own nested <Switch>, so this
                          route must NOT be `exact`. */}
                      <PrivateRoute path="/profile/settings">
                        <SettingsLayout />
                      </PrivateRoute>

                      {/* Wave 1 shipped /profile/account and linked it from the
                          Toolbar and PortfolioOverview's own header. It is now
                          the Account tab of the settings hub; the old path is
                          kept working so those links and any bookmarks survive. */}
                      <Route exact path="/profile/account">
                        <Redirect to="/profile/settings/account" />
                      </Route>

                      {/* Stripe's <Elements> provider used to wrap this route
                          from OUTSIDE, as a direct child of the <Switch>. A
                          react-router v5 <Switch> renders the FIRST child whose
                          `path` matches and treats a child with NO `path` as a
                          match for everything — so <Elements> was a catch-all:
                          it swallowed every URL under /profile that no earlier
                          route had claimed (e.g. a typo'd path rendered the
                          pricing table), and any route declared after it was
                          unreachable. The provider now sits INSIDE the route,
                          wrapping only the screen that needs it, so <Switch>
                          sees a normal path-bearing child.

                          Anything added below this line is reachable. */}
                      <PrivateRoute path="/profile/subscription">
                        <Elements stripe={stripePromise}>
                          <Prices />
                        </Elements>
                      </PrivateRoute>

                      {/* ADD NEW /profile ROUTES ABOVE THIS LINE.
                          With the <Elements> catch-all gone, an unrecognised
                          /profile/* URL matched nothing at all and rendered a
                          blank page inside the chrome. This last-child
                          <Redirect> (no `from`, so it matches anything that got
                          this far) sends it to the portfolio instead. */}
                      <Redirect to="/profile/portfolio" />
                    </Switch>
                    </RouteErrorBoundary>
                  </Main>
                </Route>
              </Switch>
              </Suspense>
              </OnboardingProvider>
            </Router>
          </PersistGate>
        </Provider>
      </ThemeProvider>
      </GoogleOAuthProvider>
    );
  }
}

export default App;